import React from 'react';
import PageHeader from '../components/layout/PageHeader';
import { GraduationCapIcon, AwardIcon, ClipboardCheckIcon, CalendarIcon, ArrowRightIcon, CheckIcon, AlertCircleIcon } from 'lucide-react';
import Button from '../components/ui/Button';
import Tabs from '../components/ui/Tabs';
interface ScholarshipType {
  id: string;
  title: string;
  description: string;
  eligibility: string[];
  benefits: string[];
  application: string;
  deadline: string;
  image: string;
}
const Scholarships = () => {
  // Sample scholarship data - Remove Caucasian images
  const scholarships: ScholarshipType[] = [{
    id: 'merit',
    title: 'Academic Merit Scholarship',
    description: 'The Academic Merit Scholarship is awarded to students who demonstrate exceptional academic performance. This scholarship recognizes and rewards students who have consistently maintained high academic standards.',
    eligibility: ['Minimum of 80% aggregate score in the previous academic year', 'Exemplary conduct and character', 'Active participation in school activities', 'Must be a current student of Isonyin Grammar School', 'Must not be a recipient of any other scholarship'],
    benefits: ['Full tuition waiver for one academic year', 'Free textbooks and learning materials', 'Recognition at the annual Speech and Prize-Giving Day'],
    application: 'Applications are reviewed at the end of each academic year. Students who meet the eligibility criteria are automatically considered based on their academic records. No separate application is required.',
    deadline: 'Not applicable (automatic consideration)',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/3iuYwPBfNvbAqWiL5UGdQD/WhatsApp_Image_2025-08-14_at_15.45.35_%283%29.jpg'
  }, {
    id: 'alumni',
    title: 'Alumni Association Scholarship',
    description: 'Funded by the Isonyin Grammar School Alumni Association, this scholarship supports academically excellent students from financially disadvantaged backgrounds. The scholarship aims to ensure that deserving students can continue their education regardless of financial constraints.',
    eligibility: ['Minimum of 70% aggregate score in the previous academic year', 'Demonstrated financial need', 'Exemplary conduct and character', 'Must be a current student of Isonyin Grammar School', 'Letter of recommendation from a teacher or school counselor'],
    benefits: ['Full tuition waiver for one academic year', 'Boarding fees subsidy (where applicable)', 'School uniform and supplies allowance', 'Mentorship by an alumni member'],
    application: "Interested students must complete the Alumni Association Scholarship application form, available from the school's administrative office or downloadable from the school website. The completed form, along with supporting documents, should be submitted to the Scholarship Committee.",
    deadline: 'July 31 (for the upcoming academic year)',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/nn93xnwTcDMxPXw6huLouK/WhatsApp_Image_2025-08-14_at_15.45.40_%281%29.jpg'
  }, {
    id: 'sports',
    title: 'Sports Excellence Scholarship',
    description: 'The Sports Excellence Scholarship recognizes and supports students who have demonstrated exceptional talent and achievement in sports. This scholarship aims to nurture sporting talents while ensuring that student-athletes maintain good academic standing.',
    eligibility: ['Demonstrated exceptional ability in one or more sports', 'Representation of the school in inter-school competitions', 'Minimum of 60% aggregate score in academic subjects', 'Good conduct and sportsmanship', "Recommendation from the school's Sports Department"],
    benefits: ['Partial tuition waiver (50%) for one academic year', 'Sports equipment and kit allowance', 'Access to specialized coaching and training', 'Support for participation in external competitions'],
    application: "Students must be nominated by the school's Sports Department or can self-nominate by submitting an application form along with a portfolio of sporting achievements. The Sports Excellence Committee reviews all applications and makes recommendations to the Scholarship Board.",
    deadline: 'August 15 (for the upcoming academic year)',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8v7HegjxEmi2KSxNec1Zxz/WhatsApp_Image_2025-10-24_at_10.01.25.jpg'
  }, {
    id: 'arts',
    title: 'Creative Arts Scholarship',
    description: 'The Creative Arts Scholarship supports students who demonstrate exceptional talent in visual arts, performing arts, music, or creative writing. This scholarship aims to nurture artistic talents and encourage cultural expression within the school community.',
    eligibility: ['Demonstrated exceptional ability in visual arts, performing arts, music, or creative writing', 'Portfolio of creative work or record of performances', 'Minimum of 60% aggregate score in academic subjects', 'Active participation in school cultural activities', "Recommendation from the school's Arts Department"],
    benefits: ['Partial tuition waiver (50%) for one academic year', 'Art supplies or instruments allowance', 'Access to specialized workshops and training', 'Opportunities to showcase work at school and external events'],
    application: 'Students must submit an application form along with a portfolio of their creative work or evidence of performances. The Arts Department reviews all applications and makes recommendations to the Scholarship Board.',
    deadline: 'August 15 (for the upcoming academic year)',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/3iuYwPBfNvbAqWiL5UGdQD/WhatsApp_Image_2025-08-14_at_15.45.35_%283%29.jpg'
  }, {
    id: 'indigent',
    title: 'Financial Need Scholarship',
    description: 'The Financial Need Scholarship provides support to academically promising students from economically disadvantaged backgrounds. This scholarship ensures that financial constraints do not prevent deserving students from accessing quality education.',
    eligibility: ['Demonstrated financial need (family income below specified threshold)', 'Minimum of 65% aggregate score in the previous academic year', 'Good conduct and character', 'Must be a current student or incoming student of Isonyin Grammar School', 'Supporting documentation of financial circumstances'],
    benefits: ['Full tuition waiver for one academic year', 'Boarding fees subsidy (where applicable)', 'School uniform, textbooks, and supplies allowance', 'Academic support and counseling'],
    application: "Applicants must complete the Financial Need Scholarship application form, available from the school's administrative office. The form must be submitted along with supporting financial documentation, academic records, and a personal statement.",
    deadline: 'July 31 (for the upcoming academic year)',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/3iuYwPBfNvbAqWiL5UGdQD/WhatsApp_Image_2025-08-14_at_15.45.35_%283%29.jpg'
  }, {
    id: 'special',
    title: "Principal's Special Award",
    description: "The Principal's Special Award is a discretionary scholarship awarded to students who have shown remarkable improvement in academic performance, demonstrated exceptional leadership qualities, or overcome significant challenges to excel in their studies.",
    eligibility: ['Significant improvement in academic performance over the previous year', 'OR demonstrated exceptional leadership qualities in school activities', 'OR overcome significant personal challenges while maintaining good academic standing', 'Exemplary conduct and character', 'Recommendation from at least two teachers'],
    benefits: ['Partial tuition waiver (varies based on individual circumstances)', 'Recognition at the annual Speech and Prize-Giving Day', 'Mentorship opportunities', 'Leadership development support'],
    application: 'Students are nominated by teachers or school administrators. In exceptional cases, students may self-nominate by submitting a letter to the Principal explaining their circumstances and achievements. The Principal, in consultation with the Scholarship Board, makes the final decision.',
    deadline: 'Nominations accepted throughout the academic year',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/3iuYwPBfNvbAqWiL5UGdQD/WhatsApp_Image_2025-08-14_at_15.45.35_%283%29.jpg'
  }];
  return <div className="min-h-screen bg-white" data-id="element-6338">
      <PageHeader title="Scholarships & Financial Aid" description="Discover scholarship opportunities that make quality education accessible to deserving students at Isonyin Grammar School." backgroundImage="https://uploadthingy.s3.us-west-1.amazonaws.com/guWk5XsDGYmXXcoEtCyUpd/WhatsApp_Image_2025-10-24_at_11.30.12_%281%29.jpg" breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'Scholarships',
      path: '/scholarships'
    }]} data-id="element-6339" />
      <div className="container mx-auto px-4 py-12" data-id="element-6340">
        {/* Introduction Section */}
        <section className="mb-16" data-id="element-6341">
          <div className="text-center mb-10" data-id="element-6342">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-6343">
              Our Commitment to Accessible Education
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-6344"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-6345">
              At Isonyin Grammar School, we believe that financial circumstances
              should not prevent talented and motivated students from accessing
              quality education. Our scholarship programs aim to recognize and
              reward academic excellence, special talents, and provide support
              to deserving students from economically disadvantaged backgrounds.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-6346">
            <div data-id="element-6347">
              <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-6348">
                Our scholarship programs are designed to be transparent, fair,
                and accessible. Each year, we award scholarships to students
                based on various criteria including academic merit, financial
                need, sporting excellence, artistic talent, and special
                circumstances.
              </p>
              <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-6349">
                These scholarships are funded through the school's endowment,
                contributions from our alumni association, corporate
                partnerships, and individual donors who share our commitment to
                educational excellence and accessibility.
              </p>
              <p className="text-gray-700 leading-relaxed" data-id="element-6350">
                We encourage all eligible students to apply for these
                opportunities. Our Scholarship Committee is dedicated to
                identifying deserving recipients and ensuring that the
                application process is straightforward and supportive.
              </p>
              <div className="mt-8 grid grid-cols-2 gap-4" data-id="element-6351">
                <div className="bg-blue-50 p-4 rounded-lg" data-id="element-6352">
                  <div className="text-blue-900 font-bold mb-2" data-id="element-6353">
                    ₦25+ Million
                  </div>
                  <p className="text-sm text-gray-600" data-id="element-6354">
                    Annual scholarship funds
                  </p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg" data-id="element-6355">
                  <div className="text-blue-900 font-bold mb-2" data-id="element-6356">150+</div>
                  <p className="text-sm text-gray-600" data-id="element-6357">
                    Students supported annually
                  </p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg" data-id="element-6358">
                  <div className="text-blue-900 font-bold mb-2" data-id="element-6359">6</div>
                  <p className="text-sm text-gray-600" data-id="element-6360">
                    Scholarship categories
                  </p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg" data-id="element-6361">
                  <div className="text-blue-900 font-bold mb-2" data-id="element-6362">25%</div>
                  <p className="text-sm text-gray-600" data-id="element-6363">
                    Of students receive some aid
                  </p>
                </div>
              </div>
            </div>
            <div data-id="element-6364">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3iuYwPBfNvbAqWiL5UGdQD/WhatsApp_Image_2025-08-14_at_15.45.35_%283%29.jpg" alt="Students in classroom" className="w-full h-auto rounded-lg shadow-md mb-6" data-id="element-6365" />
              <div className="bg-blue-900 p-6 rounded-lg text-white" data-id="element-6366">
                <h3 className="text-xl font-bold mb-4" data-id="element-6367">Scholarship Impact</h3>
                <p className="mb-4" data-id="element-6368">
                  "The scholarship I received made it possible for me to
                  continue my education at Isonyin Grammar School despite my
                  family's financial challenges. Today, I am a medical doctor
                  serving my community. Education truly transforms lives."
                </p>
                <div className="flex items-center" data-id="element-6369">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/8M2i6Lt61u8AiKMZqhL6Xz/WhatsApp_Image_2025-10-24_at_10.01.22.jpg" alt="Dr. Adeyemi" className="w-12 h-12 rounded-full object-cover mr-3" data-id="element-6370" />
                  <div data-id="element-6371">
                    <div className="font-bold" data-id="element-6372">Dr. Oluwasegun Adeyemi</div>
                    <div className="text-sm text-blue-200" data-id="element-6373">
                      Alumni, Class of 2000
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Scholarship Types Section */}
        <section className="mb-16" data-id="element-6374">
          <div className="text-center mb-10" data-id="element-6375">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-6376">
              Available Scholarships
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-6377"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-6378">
              Explore the various scholarship opportunities available at Isonyin
              Grammar School. Each scholarship has specific eligibility
              criteria, benefits, and application procedures.
            </p>
          </div>
          <Tabs tabs={scholarships.map(scholarship => ({
          id: scholarship.id,
          label: scholarship.title,
          content: <div className="py-6" data-id="element-6380">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-6381">
                    <div className="lg:col-span-1" data-id="element-6382">
                      <img src={scholarship.image} alt={scholarship.title} className="w-full h-64 object-cover rounded-lg shadow-md mb-6" data-id="element-6383" />
                      <div className="bg-blue-50 p-6 rounded-lg" data-id="element-6384">
                        <h3 className="font-bold text-blue-900 mb-4 flex items-center" data-id="element-6385">
                          <CalendarIcon className="h-5 w-5 mr-2" data-id="element-6386" />
                          Application Deadline
                        </h3>
                        <p className="text-gray-700 mb-6" data-id="element-6387">
                          {scholarship.deadline}
                        </p>
                        <h3 className="font-bold text-blue-900 mb-4 flex items-center" data-id="element-6388">
                          <ClipboardCheckIcon className="h-5 w-5 mr-2" data-id="element-6389" />
                          How to Apply
                        </h3>
                        <p className="text-gray-700" data-id="element-6390">
                          {scholarship.application}
                        </p>
                      </div>
                    </div>
                    <div className="lg:col-span-2" data-id="element-6391">
                      <h3 className="text-2xl font-bold text-blue-900 mb-4" data-id="element-6392">
                        {scholarship.title}
                      </h3>
                      <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-6393">
                        {scholarship.description}
                      </p>
                      <div className="mb-6" data-id="element-6394">
                        <h4 className="text-lg font-bold text-blue-900 mb-3 flex items-center" data-id="element-6395">
                          <AwardIcon className="h-5 w-5 mr-2" data-id="element-6396" />
                          Eligibility Criteria
                        </h4>
                        <ul className="space-y-2" data-id="element-6397">
                          {scholarship.eligibility.map((item, index) => <li key={index} className="flex items-start" data-id="element-6398">
                              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2" data-id="element-6399">
                                <CheckIcon className="h-4 w-4 text-blue-700" data-id="element-6400" />
                              </div>
                              <span className="text-gray-700" data-id="element-6401">{item}</span>
                            </li>)}
                        </ul>
                      </div>
                      <div data-id="element-6402">
                        <h4 className="text-lg font-bold text-blue-900 mb-3 flex items-center" data-id="element-6403">
                          <GraduationCapIcon className="h-5 w-5 mr-2" data-id="element-6404" />
                          Scholarship Benefits
                        </h4>
                        <ul className="space-y-2" data-id="element-6405">
                          {scholarship.benefits.map((item, index) => <li key={index} className="flex items-start" data-id="element-6406">
                              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2" data-id="element-6407">
                                <CheckIcon className="h-4 w-4 text-blue-700" data-id="element-6408" />
                              </div>
                              <span className="text-gray-700" data-id="element-6409">{item}</span>
                            </li>)}
                        </ul>
                      </div>
                      <div className="mt-8 flex justify-center" data-id="element-6410">
                        <Button href={`/scholarships/${scholarship.id}/apply`} variant="primary" data-id="element-6411">
                          Apply for This Scholarship
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
        }))} variant="boxed" data-id="element-6379" />
        </section>
        {/* Application Process Section */}
        <section className="mb-16" data-id="element-6412">
          <div className="text-center mb-10" data-id="element-6413">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-6414">
              Scholarship Application Process
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-6415"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-6416">
              Understanding the application process is key to successfully
              securing a scholarship. Follow these steps to apply for
              scholarships at Isonyin Grammar School.
            </p>
          </div>
          <div className="max-w-4xl mx-auto" data-id="element-6417">
            <div className="relative" data-id="element-6418">
              {/* Timeline line */}
              <div className="absolute left-0 md:left-1/2 top-0 bottom-0 w-0.5 bg-blue-200 transform md:translate-x-0 translate-x-7" data-id="element-6419"></div>
              {/* Timeline items */}
              <div className="space-y-12" data-id="element-6420">
                {/* Step 1 */}
                <div className="relative flex flex-col md:flex-row items-start" data-id="element-6421">
                  <div className="flex items-center md:w-1/2 md:justify-end md:pr-8 pb-8 md:pb-0" data-id="element-6422">
                    <div className="md:text-right" data-id="element-6423">
                      <h3 className="text-xl font-bold text-blue-900 mb-2" data-id="element-6424">
                        Research Available Scholarships
                      </h3>
                      <p className="text-gray-700" data-id="element-6425">
                        Review the available scholarships and identify those for
                        which you meet the eligibility criteria. Pay close
                        attention to the requirements, benefits, and application
                        deadlines.
                      </p>
                    </div>
                  </div>
                  <div className="absolute left-0 md:left-1/2 top-0 h-8 w-8 rounded-full bg-blue-900 text-white flex items-center justify-center transform md:translate-x-[-50%] translate-x-[-50%] z-10" data-id="element-6426">
                    1
                  </div>
                  <div className="md:w-1/2 md:pl-8 pl-16" data-id="element-6427"></div>
                </div>
                {/* Step 2 */}
                <div className="relative flex flex-col md:flex-row items-start" data-id="element-6428">
                  <div className="md:w-1/2 md:text-right md:pr-8" data-id="element-6429"></div>
                  <div className="absolute left-0 md:left-1/2 top-0 h-8 w-8 rounded-full bg-blue-900 text-white flex items-center justify-center transform md:translate-x-[-50%] translate-x-[-50%] z-10" data-id="element-6430">
                    2
                  </div>
                  <div className="flex items-center md:w-1/2 md:pl-8 pl-16 pb-8 md:pb-0" data-id="element-6431">
                    <div data-id="element-6432">
                      <h3 className="text-xl font-bold text-blue-900 mb-2" data-id="element-6433">
                        Obtain Application Form
                      </h3>
                      <p className="text-gray-700" data-id="element-6434">
                        Download the application form from our website or
                        collect it from the school's administrative office.
                        Ensure you have the correct form for the specific
                        scholarship you're applying for.
                      </p>
                    </div>
                  </div>
                </div>
                {/* Step 3 */}
                <div className="relative flex flex-col md:flex-row items-start" data-id="element-6435">
                  <div className="flex items-center md:w-1/2 md:justify-end md:pr-8 pb-8 md:pb-0" data-id="element-6436">
                    <div className="md:text-right" data-id="element-6437">
                      <h3 className="text-xl font-bold text-blue-900 mb-2" data-id="element-6438">
                        Complete the Application
                      </h3>
                      <p className="text-gray-700" data-id="element-6439">
                        Fill out the application form completely and accurately.
                        Incomplete applications may not be considered. Be sure
                        to provide all requested information and answer all
                        questions thoroughly.
                      </p>
                    </div>
                  </div>
                  <div className="absolute left-0 md:left-1/2 top-0 h-8 w-8 rounded-full bg-blue-900 text-white flex items-center justify-center transform md:translate-x-[-50%] translate-x-[-50%] z-10" data-id="element-6440">
                    3
                  </div>
                  <div className="md:w-1/2 md:pl-8 pl-16" data-id="element-6441"></div>
                </div>
                {/* Step 4 */}
                <div className="relative flex flex-col md:flex-row items-start" data-id="element-6442">
                  <div className="md:w-1/2 md:text-right md:pr-8" data-id="element-6443"></div>
                  <div className="absolute left-0 md:left-1/2 top-0 h-8 w-8 rounded-full bg-blue-900 text-white flex items-center justify-center transform md:translate-x-[-50%] translate-x-[-50%] z-10" data-id="element-6444">
                    4
                  </div>
                  <div className="flex items-center md:w-1/2 md:pl-8 pl-16 pb-8 md:pb-0" data-id="element-6445">
                    <div data-id="element-6446">
                      <h3 className="text-xl font-bold text-blue-900 mb-2" data-id="element-6447">
                        Gather Supporting Documents
                      </h3>
                      <p className="text-gray-700" data-id="element-6448">
                        Collect all required supporting documents, which may
                        include academic records, financial statements,
                        recommendation letters, personal statements, or
                        portfolios of work (for arts or sports scholarships).
                      </p>
                    </div>
                  </div>
                </div>
                {/* Step 5 */}
                <div className="relative flex flex-col md:flex-row items-start" data-id="element-6449">
                  <div className="flex items-center md:w-1/2 md:justify-end md:pr-8 pb-8 md:pb-0" data-id="element-6450">
                    <div className="md:text-right" data-id="element-6451">
                      <h3 className="text-xl font-bold text-blue-900 mb-2" data-id="element-6452">
                        Submit Application
                      </h3>
                      <p className="text-gray-700" data-id="element-6453">
                        Submit your completed application form and all
                        supporting documents before the deadline. Late
                        applications are generally not accepted. You can submit
                        in person at the administrative office or through the
                        online portal.
                      </p>
                    </div>
                  </div>
                  <div className="absolute left-0 md:left-1/2 top-0 h-8 w-8 rounded-full bg-blue-900 text-white flex items-center justify-center transform md:translate-x-[-50%] translate-x-[-50%] z-10" data-id="element-6454">
                    5
                  </div>
                  <div className="md:w-1/2 md:pl-8 pl-16" data-id="element-6455"></div>
                </div>
                {/* Step 6 */}
                <div className="relative flex flex-col md:flex-row items-start" data-id="element-6456">
                  <div className="md:w-1/2 md:text-right md:pr-8" data-id="element-6457"></div>
                  <div className="absolute left-0 md:left-1/2 top-0 h-8 w-8 rounded-full bg-blue-900 text-white flex items-center justify-center transform md:translate-x-[-50%] translate-x-[-50%] z-10" data-id="element-6458">
                    6
                  </div>
                  <div className="flex items-center md:w-1/2 md:pl-8 pl-16 pb-8 md:pb-0" data-id="element-6459">
                    <div data-id="element-6460">
                      <h3 className="text-xl font-bold text-blue-900 mb-2" data-id="element-6461">
                        Interview (if required)
                      </h3>
                      <p className="text-gray-700" data-id="element-6462">
                        Some scholarships require an interview as part of the
                        selection process. If selected for an interview, prepare
                        thoroughly and present yourself professionally. The
                        interview is an opportunity to elaborate on your
                        application and demonstrate your suitability for the
                        scholarship.
                      </p>
                    </div>
                  </div>
                </div>
                {/* Step 7 */}
                <div className="relative flex flex-col md:flex-row items-start" data-id="element-6463">
                  <div className="flex items-center md:w-1/2 md:justify-end md:pr-8" data-id="element-6464">
                    <div className="md:text-right" data-id="element-6465">
                      <h3 className="text-xl font-bold text-blue-900 mb-2" data-id="element-6466">
                        Await Decision
                      </h3>
                      <p className="text-gray-700" data-id="element-6467">
                        The Scholarship Committee will review all applications
                        and make decisions based on the established criteria.
                        All applicants will be notified of the outcome, whether
                        successful or not. Successful applicants will receive
                        further instructions regarding the acceptance process.
                      </p>
                    </div>
                  </div>
                  <div className="absolute left-0 md:left-1/2 top-0 h-8 w-8 rounded-full bg-blue-900 text-white flex items-center justify-center transform md:translate-x-[-50%] translate-x-[-50%] z-10" data-id="element-6468">
                    7
                  </div>
                  <div className="md:w-1/2 md:pl-8 pl-16" data-id="element-6469"></div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* FAQs Section */}
        <section className="mb-16" data-id="element-6470">
          <div className="text-center mb-10" data-id="element-6471">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-6472">
              Frequently Asked Questions
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-6473"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-6474">
              Find answers to common questions about our scholarship programs.
            </p>
          </div>
          <div className="max-w-3xl mx-auto" data-id="element-6475">
            <div className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-6476">
              <div className="divide-y divide-gray-200" data-id="element-6477">
                <div className="p-6" data-id="element-6478">
                  <h3 className="font-bold text-lg text-blue-900 mb-2" data-id="element-6479">
                    Can I apply for multiple scholarships?
                  </h3>
                  <p className="text-gray-700" data-id="element-6480">
                    Yes, you can apply for multiple scholarships if you meet the
                    eligibility criteria for each. However, in most cases, a
                    student can only be awarded one scholarship at a time. If
                    selected for multiple scholarships, you will typically be
                    awarded the one that provides the greatest benefit.
                  </p>
                </div>
                <div className="p-6" data-id="element-6481">
                  <h3 className="font-bold text-lg text-blue-900 mb-2" data-id="element-6482">
                    Are scholarships automatically renewed each year?
                  </h3>
                  <p className="text-gray-700" data-id="element-6483">
                    Most scholarships are awarded for one academic year and
                    require renewal applications. Renewal is typically
                    contingent on maintaining certain academic standards, good
                    conduct, and continued eligibility based on the
                    scholarship's criteria. Some scholarships may have different
                    renewal requirements, so it's important to check the
                    specific terms of your award.
                  </p>
                </div>
                <div className="p-6" data-id="element-6484">
                  <h3 className="font-bold text-lg text-blue-900 mb-2" data-id="element-6485">
                    What happens if my financial circumstances change during the
                    academic year?
                  </h3>
                  <p className="text-gray-700" data-id="element-6486">
                    If your financial circumstances change significantly during
                    the academic year (e.g., loss of family income, unexpected
                    medical expenses), you can apply for emergency financial aid
                    or request a review of your scholarship award. Contact the
                    Scholarship Office to discuss your situation and the
                    available options.
                  </p>
                </div>
                <div className="p-6" data-id="element-6487">
                  <h3 className="font-bold text-lg text-blue-900 mb-2" data-id="element-6488">
                    How competitive are the scholarships?
                  </h3>
                  <p className="text-gray-700" data-id="element-6489">
                    The competitiveness varies by scholarship type. Merit-based
                    scholarships are highly competitive and awarded to the
                    top-performing students. Need-based scholarships consider
                    both academic performance and financial circumstances. We
                    encourage all eligible students to apply, as the number of
                    scholarships awarded each year may vary based on available
                    funding.
                  </p>
                </div>
                <div className="p-6" data-id="element-6490">
                  <h3 className="font-bold text-lg text-blue-900 mb-2" data-id="element-6491">
                    Can international students apply for scholarships?
                  </h3>
                  <p className="text-gray-700" data-id="element-6492">
                    Yes, international students are eligible to apply for most
                    scholarships. However, some scholarships may have specific
                    citizenship or residency requirements. International
                    students should carefully review the eligibility criteria
                    for each scholarship before applying.
                  </p>
                </div>
                <div className="p-6" data-id="element-6493">
                  <h3 className="font-bold text-lg text-blue-900 mb-2" data-id="element-6494">
                    What if I miss the application deadline?
                  </h3>
                  <p className="text-gray-700" data-id="element-6495">
                    Late applications are generally not accepted. It is
                    important to submit your complete application and all
                    supporting documents before the specified deadline. In
                    exceptional circumstances (e.g., serious illness, family
                    emergency), you may contact the Scholarship Office to
                    inquire about possible accommodations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Contact Section */}
        <section className="mb-16" data-id="element-6496">
          <div className="bg-blue-50 p-8 rounded-lg" data-id="element-6497">
            <div className="text-center mb-8" data-id="element-6498">
              <h2 className="text-2xl font-bold text-blue-900 mb-3" data-id="element-6499">
                Need Help with Your Scholarship Application?
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-6500">
                Our Scholarship Office is here to assist you with any questions
                or concerns about the scholarship application process.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-6501">
              <div className="bg-white p-6 rounded-lg shadow-sm" data-id="element-6502">
                <div className="flex items-center mb-4" data-id="element-6503">
                  <div className="p-3 bg-blue-100 rounded-full mr-3" data-id="element-6504">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-6505">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" data-id="element-6506" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-blue-900" data-id="element-6507">Email</h3>
                </div>
                <p className="text-gray-700 mb-2" data-id="element-6508">For general inquiries:</p>
                <a href="mailto:scholarships@isonyingrammarschool.edu.ng" className="text-blue-700 hover:text-blue-900" data-id="element-6509">
                  scholarships@isonyingrammarschool.edu.ng
                </a>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm" data-id="element-6510">
                <div className="flex items-center mb-4" data-id="element-6511">
                  <div className="p-3 bg-blue-100 rounded-full mr-3" data-id="element-6512">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-6513">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" data-id="element-6514" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-blue-900" data-id="element-6515">Phone</h3>
                </div>
                <p className="text-gray-700 mb-2" data-id="element-6516">Scholarship Office:</p>
                <a href="tel:+2348031234567" className="text-blue-700 hover:text-blue-900" data-id="element-6517">
                  +234 (0) 803 123 4567
                </a>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm" data-id="element-6518">
                <div className="flex items-center mb-4" data-id="element-6519">
                  <div className="p-3 bg-blue-100 rounded-full mr-3" data-id="element-6520">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-6521">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" data-id="element-6522" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" data-id="element-6523" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-blue-900" data-id="element-6524">Visit</h3>
                </div>
                <p className="text-gray-700 mb-2" data-id="element-6525">
                  Scholarship Office, Administrative Block
                </p>
                <p className="text-gray-700" data-id="element-6526">
                  Open Monday-Friday, 9:00 AM - 3:00 PM
                </p>
              </div>
            </div>
          </div>
        </section>
        {/* Donor Section */}
        <section data-id="element-6527">
          <div className="bg-blue-900 text-white p-8 rounded-lg text-center" data-id="element-6528">
            <h2 className="text-2xl font-bold mb-4" data-id="element-6529">
              Support Our Scholarship Fund
            </h2>
            <p className="mb-6 max-w-2xl mx-auto" data-id="element-6530">
              Your contribution to our scholarship fund can help provide quality
              education to deserving students who might otherwise not have the
              opportunity. Join our donors in making a difference in the lives
              of young people.
            </p>
            <div className="flex flex-wrap justify-center gap-4" data-id="element-6531">
              <Button href="/support" variant="primary" size="lg" data-id="element-6532">
                Donate Now
              </Button>
              <Button href="/support/scholarship-fund" variant="outline" size="lg" className="border-white text-white hover:bg-white/10" data-id="element-6533">
                Learn More About Our Fund
              </Button>
            </div>
          </div>
        </section>
      </div>
    </div>;
};
export default Scholarships;